<!--
  Duplicates Page
  Shows all detected duplicate documents grouped by their original.
  Bulk multiselect: keep (unmark) or delete selected duplicates.
-->
<script lang="ts">
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { _ } from 'svelte-i18n';
  import { api, type DuplicateGroupDto, type DuplicateDocumentDto } from '../lib/api';
  import ExternalLink from 'lucide-svelte/icons/external-link';
  import FileText from 'lucide-svelte/icons/file-text';
  import Trash2 from 'lucide-svelte/icons/trash-2';
  import FileCheck from 'lucide-svelte/icons/file-check';
  import PageHeader from '../lib/components/layout/PageHeader.svelte';
  import { Button, Spinner, Modal, EmptyState } from '../lib/components/ui/index';
  import { toastStore } from '../lib/stores/toastStore';
  import { userSettings } from '../lib/stores/settingsStore';
  import { activeHouseholdStore } from '../lib/stores/activeHouseholdStore';
  import { formatDate } from '../lib/settings';
  import SignedThumbnailImage from '../lib/components/SignedThumbnailImage.svelte';

  let groups: DuplicateGroupDto[] = [];
  let totalDuplicates = 0;
  let loading = true;
  let error = false;

  // --- Multiselect state ---
  let checkedIds = new Set<string>();
  $: checkedCount = checkedIds.size;

  // All duplicate document ids across all groups (originals are not selectable)
  $: allDuplicateIds = groups.flatMap((g) => g.duplicates.map((d) => d.id));
  $: allChecked = allDuplicateIds.length > 0 && allDuplicateIds.every((id) => checkedIds.has(id));

  function toggleCheckAll() {
    if (allChecked) {
      checkedIds = new Set();
    } else {
      checkedIds = new Set(allDuplicateIds);
    }
  }

  function toggleCheck(id: string) {
    const next = new Set(checkedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    checkedIds = next;
  }

  function clearSelection() {
    checkedIds = new Set();
  }

  // --- Bulk action confirmation ---
  type BulkActionType = 'keep' | 'delete';
  let pendingBulkAction: BulkActionType | null = null;
  let bulkActionInProgress = false;

  function requestBulkAction(type: BulkActionType) {
    pendingBulkAction = type;
  }

  async function executeBulkAction() {
    if (!pendingBulkAction) return;
    const type = pendingBulkAction;
    const ids = [...checkedIds];
    if (ids.length === 0) return;

    bulkActionInProgress = true;
    try {
      if (type === 'keep') {
        const result = await api.reports.unmarkDuplicatesBatch(ids);
        if (result.unmarkedCount < result.totalRequested) {
          const failed = result.totalRequested - result.unmarkedCount;
          toastStore.error(
            $_('pages.duplicates.bulk_partial_keep', {
              values: { processed: result.unmarkedCount, failed },
            }),
          );
          // Partial failure: resync from server so failed rows are not silently
          // dropped from the list and totalDuplicates stays accurate.
          await loadDuplicates();
        } else {
          removeProcessedFromGroups(ids);
          toastStore.success(
            $_('pages.duplicates.bulk_keep_success', { values: { count: result.unmarkedCount } }),
          );
        }
      } else {
        const result = await api.documents.deleteDocumentsBatch(ids);
        if (result.deletedCount < result.totalRequested) {
          const failed = result.totalRequested - result.deletedCount;
          toastStore.error(
            $_('pages.duplicates.bulk_partial_delete', {
              values: { processed: result.deletedCount, failed },
            }),
          );
          await loadDuplicates();
        } else {
          removeProcessedFromGroups(ids);
          toastStore.success(
            $_('pages.duplicates.bulk_delete_success', { values: { count: result.deletedCount } }),
          );
        }
      }
    } catch {
      // global error handler shows toast
    } finally {
      bulkActionInProgress = false;
      pendingBulkAction = null;
      clearSelection();
    }
  }

  /**
   * Remove successfully processed ids from groups and update totalDuplicates.
   * Only removes entries that were actually in the selection. Partial failures
   * (count < totalRequested) are surfaced via toast; we do NOT silently remove
   * non-processed rows — the reload message tells the user what happened.
   */
  function removeProcessedFromGroups(ids: string[]) {
    const removed = new Set(ids);
    let removedCount = 0;

    groups = groups
      .map((g) => {
        const before = g.duplicates.length;
        const filteredDups = g.duplicates.filter((d) => !removed.has(d.id));
        removedCount += before - filteredDups.length;
        return { ...g, duplicates: filteredDups };
      })
      .filter((g) => g.duplicates.length > 0);

    totalDuplicates = Math.max(0, totalDuplicates - removedCount);
  }

  function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  /**
   * Defensive dedup (E3): even though the backend already dedupes (E1/E2), the frontend must
   * not crash (`each_key_duplicate`) if a caching/read-model lag ever slips a repeated
   * original.id or duplicate.id through. Dedupe by id, first occurrence wins.
   */
  function dedupeGroups(input: DuplicateGroupDto[]): DuplicateGroupDto[] {
    const seenOriginalIds = new Set<string>();
    const result: DuplicateGroupDto[] = [];

    for (const group of input) {
      if (seenOriginalIds.has(group.original.id)) continue;
      seenOriginalIds.add(group.original.id);

      const seenDuplicateIds = new Set<string>();
      const duplicates = group.duplicates.filter((d) => {
        if (seenDuplicateIds.has(d.id)) return false;
        seenDuplicateIds.add(d.id);
        return true;
      });

      result.push({ ...group, duplicates });
    }

    return result;
  }

  async function loadDuplicates() {
    loading = true;
    error = false;
    checkedIds = new Set();
    try {
      const response = await api.reports.getDuplicateGroups($activeHouseholdStore || undefined);
      groups = dedupeGroups(response.groups);
      totalDuplicates = response.totalDuplicates;
    } catch {
      // toast comes from the global error handler; the page only shows the state (Spec 098)
      error = true;
    } finally {
      loading = false;
    }
  }

  // Name click navigates in the same tab (consistent with Browse/Inbox).
  function openDocument(doc: DuplicateDocumentDto) {
    navigate(`/documents/${doc.slug || doc.id}`);
  }

  // The explicit ExternalLink icon opens a new tab so the current selection is preserved.
  function openInNewTab(doc: DuplicateDocumentDto) {
    window.open(`/documents/${doc.slug || doc.id}`, '_blank');
  }

  onMount(() => {
    loadDuplicates();
  });

  // Reload when household changes
  let prevHousehold = $activeHouseholdStore;
  $: if ($activeHouseholdStore !== prevHousehold) {
    prevHousehold = $activeHouseholdStore;
    loadDuplicates();
  }
</script>

<div class="max-w-5xl mx-auto">
  <PageHeader title={$_('pages.duplicates.title')} subtitle={$_('pages.duplicates.subtitle')} />

  {#if loading}
    <div class="flex justify-center py-16">
      <Spinner />
    </div>
  {:else if error}
    <div
      data-testid="duplicates-error"
      class="flex flex-col items-center justify-center py-12 px-4 text-center"
    >
      <p class="text-sm text-text-secondary mb-4">{$_('pages.duplicates.load_error')}</p>
      <Button data-testid="duplicates-retry" variant="primary" on:click={loadDuplicates}>
        {$_('pages.error.retry')}
      </Button>
    </div>
  {:else if groups.length === 0}
    <EmptyState
      title={$_('pages.duplicates.empty_title')}
      description={$_('pages.duplicates.empty_desc')}
    />
  {:else}
    <!-- Global header with select-all + count -->
    <div class="flex items-center gap-3 mb-3">
      <input
        type="checkbox"
        class="accent-accent w-4 h-4 cursor-pointer"
        checked={allChecked}
        on:change={toggleCheckAll}
        title={allChecked ? $_('pages.duplicates.deselect_all') : $_('pages.duplicates.select_all')}
        aria-label={allChecked
          ? $_('pages.duplicates.deselect_all')
          : $_('pages.duplicates.select_all')}
        data-testid="duplicates-select-all"
      />
      <p class="text-sm text-text-secondary">
        {$_('pages.duplicates.total_duplicates', {
          values: { count: totalDuplicates, groups: groups.length },
        })}
      </p>
    </div>

    <!-- Group list. Bottom padding keeps the last group clear of the sticky action bar. -->
    <div class="flex flex-col gap-4 pb-24">
      {#each groups as group (group.original.id)}
        <div class="border border-border rounded-lg bg-bg-alt overflow-hidden">
          <!-- Original document header -->
          <div class="px-4 py-3 border-b border-border bg-surface">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-14 shrink-0 rounded bg-surface flex items-center justify-center overflow-hidden"
              >
                {#if group.original.thumbnailMedium}
                  <SignedThumbnailImage
                    documentId={group.original.id}
                    size="medium"
                    className="w-full h-full object-cover"
                    lazy
                  >
                    <FileText slot="fallback" class="w-5 h-5 text-text-muted" strokeWidth={1.5} />
                  </SignedThumbnailImage>
                {:else}
                  <FileText class="w-5 h-5 text-text-muted" strokeWidth={1.5} />
                {/if}
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="text-xs font-medium text-accent uppercase tracking-wide">
                    {$_('pages.duplicates.original')}
                  </span>
                </div>
                <button
                  class="text-sm font-medium text-text hover:text-accent truncate block max-w-full text-left bg-transparent border-none cursor-pointer p-0"
                  on:click={() => openDocument(group.original)}
                >
                  {group.original.displayName || group.original.fileName}
                </button>
                <div class="text-xs text-text-secondary mt-0.5">
                  {$_('pages.duplicates.uploaded_at')}
                  {formatDate(group.original.uploadedAt, $userSettings)}
                  · {formatFileSize(group.original.fileSize)}
                </div>
              </div>
              <button
                class="text-text-muted hover:text-accent transition-colors bg-transparent border-none cursor-pointer p-1"
                on:click={() => openInNewTab(group.original)}
                title={$_('pages.duplicates.open_in_new_tab')}
              >
                <ExternalLink class="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>

          <!-- Duplicate documents -->
          <div class="divide-y divide-border">
            {#each group.duplicates as dup (dup.id)}
              <div class="px-4 py-3 flex items-center gap-3">
                <!-- Checkbox -->
                <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-element-interactions -->
                <label class="flex items-center cursor-pointer" on:click|stopPropagation>
                  <input
                    type="checkbox"
                    class="accent-accent w-4 h-4 cursor-pointer"
                    checked={checkedIds.has(dup.id)}
                    on:change={() => toggleCheck(dup.id)}
                    aria-label={dup.displayName || dup.fileName}
                    data-testid="duplicates-check-{dup.id}"
                  />
                </label>

                <!-- Thumbnail -->
                <div
                  class="w-10 h-14 shrink-0 rounded bg-surface flex items-center justify-center overflow-hidden"
                >
                  {#if dup.thumbnailMedium}
                    <SignedThumbnailImage
                      documentId={dup.id}
                      size="medium"
                      className="w-full h-full object-cover"
                      lazy
                    >
                      <FileText slot="fallback" class="w-5 h-5 text-text-muted" strokeWidth={1.5} />
                    </SignedThumbnailImage>
                  {:else}
                    <FileText class="w-5 h-5 text-text-muted" strokeWidth={1.5} />
                  {/if}
                </div>

                <!-- Name + meta -->
                <div class="flex-1 min-w-0">
                  <button
                    class="text-sm font-medium text-text hover:text-accent truncate block max-w-full text-left bg-transparent border-none cursor-pointer p-0"
                    on:click={() => openDocument(dup)}
                  >
                    {dup.displayName || dup.fileName}
                  </button>
                  <div class="text-xs text-text-secondary mt-0.5">
                    {$_('pages.duplicates.uploaded_at')}
                    {formatDate(dup.uploadedAt, $userSettings)}
                    · {formatFileSize(dup.fileSize)}
                  </div>
                </div>

                <!-- Open link (navigation only, not a selection action) -->
                <button
                  class="text-text-muted hover:text-accent transition-colors bg-transparent border-none cursor-pointer p-1 shrink-0"
                  on:click={() => openInNewTab(dup)}
                  title={$_('pages.duplicates.open_in_new_tab')}
                >
                  <ExternalLink class="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            {/each}
          </div>
        </div>
      {/each}
    </div>

    <!--
      Always-visible action bar, pinned to the bottom. Rendering it unconditionally
      (buttons just toggle disabled) means the user sees the available actions on load
      and the group list never reflows when the first item is checked.
    -->
    <div
      class="sticky bottom-0 z-10 -mx-4 px-4 py-3 flex flex-wrap items-center gap-3 bg-surface/95 backdrop-blur-sm border-t border-border"
      data-testid="duplicates-bulk-bar"
    >
      <span
        class="text-sm shrink-0"
        class:text-text={checkedCount >= 1}
        class:font-medium={checkedCount >= 1}
        class:text-text-secondary={checkedCount === 0}
      >
        {#if checkedCount >= 1}
          {$_('pages.duplicates.selected_count', { values: { count: checkedCount } })}
        {:else}
          {$_('pages.duplicates.bulk_select_hint')}
        {/if}
      </span>
      <div class="flex items-center gap-2 flex-wrap ml-auto">
        <Button
          variant="primary"
          size="sm"
          disabled={checkedCount === 0}
          on:click={() => requestBulkAction('keep')}
        >
          <FileCheck class="w-4 h-4 mr-1.5" strokeWidth={1.5} />
          {$_('pages.duplicates.bulk_keep')}
        </Button>
        <Button
          variant="danger"
          size="sm"
          disabled={checkedCount === 0}
          on:click={() => requestBulkAction('delete')}
        >
          <Trash2 class="w-4 h-4 mr-1.5" strokeWidth={1.5} />
          {$_('pages.duplicates.bulk_delete')}
        </Button>
      </div>
    </div>
  {/if}
</div>

<!-- Bulk Keep Confirmation Modal -->
<Modal
  open={pendingBulkAction === 'keep'}
  title={$_('pages.duplicates.bulk_keep_confirm_title', { values: { count: checkedCount } })}
  on:close={() => (pendingBulkAction = null)}
>
  <p class="text-sm text-text-secondary mb-4">
    {$_('pages.duplicates.bulk_keep_confirm_message')}
  </p>
  <svelte:fragment slot="footer">
    <Button
      variant="primary"
      loading={bulkActionInProgress}
      disabled={bulkActionInProgress}
      on:click={executeBulkAction}
    >
      {$_('pages.duplicates.bulk_keep_confirm_button')}
    </Button>
    <Button
      variant="secondary"
      disabled={bulkActionInProgress}
      on:click={() => (pendingBulkAction = null)}
    >
      {$_('pages.settings.cancel')}
    </Button>
  </svelte:fragment>
</Modal>

<!-- Bulk Delete Confirmation Modal -->
<Modal
  open={pendingBulkAction === 'delete'}
  title={$_('pages.duplicates.bulk_delete_confirm_title', { values: { count: checkedCount } })}
  on:close={() => (pendingBulkAction = null)}
>
  <p class="text-sm text-text-secondary mb-4">
    {$_('pages.duplicates.bulk_delete_confirm_message')}
  </p>
  <svelte:fragment slot="footer">
    <Button
      variant="danger"
      loading={bulkActionInProgress}
      disabled={bulkActionInProgress}
      on:click={executeBulkAction}
    >
      {$_('pages.duplicates.bulk_delete_confirm_button')}
    </Button>
    <Button
      variant="secondary"
      disabled={bulkActionInProgress}
      on:click={() => (pendingBulkAction = null)}
    >
      {$_('pages.settings.cancel')}
    </Button>
  </svelte:fragment>
</Modal>
